import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";

const IMAGE_MS = 6000;
const FADE_S = 1.5;
const VIDEO_MAX_MS = 9000;
const cover = "absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700";

// The card's own stock image/video — used until real job media is assigned. A failed video
// falls back to its poster/still so the card is never left blank.
const Fallback = ({ s }) => {
  const [videoFailed, setVideoFailed] = useState(false);
  if (s.video && !videoFailed) {
    return (
      <video src={s.video} poster={s.videoPoster || s.image} data-testid={`service-video-${s.id}`} className={cover}
        autoPlay muted loop playsInline preload="metadata" aria-label={`${s.name} video`} onError={() => setVideoFailed(true)} />
    );
  }
  return <img src={s.videoPoster || s.image} alt={`${s.name} service`} className={cover} style={s.imagePosition ? { objectPosition: s.imagePosition } : undefined} loading="lazy" data-testid={`service-image-${s.id}`} />;
};

// Slowly rotates through the photos/videos the owner picked for a service card (only while on
// screen). "original" = the card's own picture. Nothing picked = the original picture alone.
export const ServiceCardMedia = ({ s, media }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-10% 0px" });
  const [i, setI] = useState(0);
  const items = media || [];
  const cur = items[i % Math.max(items.length, 1)];
  const next = () => setI((v) => (v + 1) % items.length);

  useEffect(() => {
    if (!inView || items.length < 2) return undefined;
    const t = setTimeout(next, cur?.kind === "video" ? VIDEO_MAX_MS : IMAGE_MS);
    return () => clearTimeout(t);
  }, [i, inView, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // keep the ref'd wrapper mounted even before media loads — useInView only observes on mount
  if (!items.length) return <div ref={ref} className="absolute inset-0"><Fallback s={s} /></div>;
  return (
    <div ref={ref} className="absolute inset-0" data-testid={`service-media-${s.id}`} data-count={items.length}>
      <AnimatePresence initial={false}>
        <motion.div key={`${cur.id}-${i}`} className="absolute inset-0"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: FADE_S, ease: "easeInOut" }}>
          {cur.kind === "original" ? <Fallback s={s} /> : cur.kind === "video" ? (
            <video src={cur.src} poster={cur.poster} className={cover} autoPlay muted playsInline preload="metadata"
              loop={items.length === 1} onEnded={items.length > 1 ? next : undefined} data-testid={`service-media-${s.id}-video`}
              aria-label={cur.caption || `${s.name} job video`} />
          ) : (
            <img src={cur.src} alt={cur.caption || `${s.name} — recent job`} className={cover} loading="lazy"
              data-testid={`service-media-${s.id}-image`} />
          )}
        </motion.div>
      </AnimatePresence>
      {items.length > 1 && (
        <div className="absolute bottom-3 right-3 z-10 flex gap-1" aria-hidden="true">
          {items.map((m, k) => (
            <span key={m.id} data-testid={`service-media-${s.id}-dot-${k}`}
              className={`h-1 transition-[width,background-color] duration-300 ${k === i % items.length ? "w-5 bg-white" : "w-2 bg-white/50"}`} />
          ))}
        </div>
      )}
    </div>
  );
};
